import { useQuery } from "@tanstack/react-query";
import { api } from "./client";
import type { BehaviorDump, BehaviorMulti, LineageRow } from "./types";

const enc = (s: string) => encodeURIComponent(s);

// Wire-trimmed lineage from the orchestrator: fitness scalars + best/mean genome for
// every gen, top_k only for recent gens. The raw lineage.jsonl bundles a 30-genome
// population per gen and grows to hundreds of MB — fetching+parsing that in the browser
// (esp. a phone) is what blanked the fitness/network/phylo/landscape views. This endpoint
// bounds the payload to a few MB while every view still renders.
async function fetchLineage(name: string): Promise<LineageRow[]> {
  const text = await api.getText(`/api/v1/runs/${enc(name)}/lineage`);
  return JSON.parse(text) as LineageRow[];
}

async function fetchBehavior(name: string): Promise<BehaviorDump | null> {
  try {
    const text = await api.getText(`/api/v1/runs/${enc(name)}/artifact/behavior_live.json`);
    return JSON.parse(text) as BehaviorDump;
  } catch {
    return null; // not written until the first new-best
  }
}

export function useLineage(name: string, live: boolean) {
  return useQuery({
    queryKey: ["lineage", name],
    queryFn: () => fetchLineage(name),
    enabled: !!name,
    refetchInterval: live ? 2500 : false,
    staleTime: 2000,
  });
}

export function useBehavior(name: string, live: boolean) {
  return useQuery({
    queryKey: ["behavior", name],
    queryFn: () => fetchBehavior(name),
    enabled: !!name,
    refetchInterval: live ? 2500 : false,
    staleTime: 2000,
  });
}

async function fetchBehaviorMulti(name: string): Promise<BehaviorMulti | null> {
  try {
    const text = await api.getText(`/api/v1/runs/${enc(name)}/artifact/behavior_multi.json`);
    return JSON.parse(text) as BehaviorMulti;
  } catch {
    return null; // only present when a placements snapshot has been generated
  }
}

export function useBehaviorMulti(name: string, live: boolean) {
  return useQuery({
    queryKey: ["behaviorMulti", name],
    queryFn: () => fetchBehaviorMulti(name),
    enabled: !!name,
    refetchInterval: live ? 4000 : false,
    staleTime: 3000,
  });
}
